// ============================================================
// Document ingest — commit client (browser -> ingest-document-corpus Edge Function).
// Hashes the source PDF (integrity anchor) and POSTs one parsed+redacted document per call.
// ============================================================

import { getSupabase } from "../supabase";
import type { ParsedDoc } from "./parseDocument";

// Dogfood default scope: Co-Defendant Defense remit (non-public).
export const DEFAULT_SCOPE_CODEFENDANT_DEFENSE = "33333333-3333-4333-8333-333333333301";

export async function sha256Hex(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  const digest = await crypto.subtle.digest("SHA-256", buf);
  return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, "0")).join("");
}

export interface DocIngestResult { job_id: string; source_label: string; status: string; evidence_kind: string }

export async function ingestDocument(
  caseId: string,
  doc: ParsedDoc,
  sourceFile: File,
  scopeIds: string[] = [DEFAULT_SCOPE_CODEFENDANT_DEFENSE],
): Promise<DocIngestResult> {
  const supabase = getSupabase();
  if (!supabase) throw new Error("Supabase not configured");
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error("Not signed in");

  const source_sha256 = await sha256Hex(sourceFile);
  const baseUrl = import.meta.env.VITE_SUPABASE_URL as string;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

  const res = await fetch(`${baseUrl}/functions/v1/ingest-document-corpus`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      "Content-Type": "application/json",
      apikey: anonKey,
    },
    body: JSON.stringify({
      case_id: caseId,
      scope_ids: scopeIds,
      source_uri: `drive:${sourceFile.name}`,
      source_sha256,
      document: doc,
    }),
  });

  const text = await res.text();
  if (!res.ok) {
    let detail = text;
    try { detail = JSON.parse(text).error ?? text; } catch { /* keep raw */ }
    throw new Error(`Ingest failed (HTTP ${res.status}): ${detail}`);
  }
  const data = JSON.parse(text) as DocIngestResult;
  if (!data.job_id) throw new Error("Ingest returned no job_id");
  return data;
}
